import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Edit2, Save, X, Plus, Trash2, Image as ImageIcon,
  Star, Eye, EyeOff, CheckCircle, ChevronDown, ChevronUp,
  Globe, Clock, Users, Tag, BookOpen, FileText, Award,
  MapPin, MessageSquare, Search,
} from "lucide-react";
import api, { BACKEND } from "@/lib/api";
import { ImageUpload } from "./Content";

// ── Helpers ───────────────────────────────────────────────────────────────────
const SI = "w-full px-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 text-sm bg-white";
const TA = `${SI} min-h-[88px] resize-y`;
const resolveImg = (url) => url ? (url.startsWith("http") ? url : `${BACKEND}${url}`) : null;

const MODES  = ["Online", "Offline", "Hybrid"];
const LEVELS = ["Beginner", "Intermediate", "Advanced", "All Levels"];
const CATEGORIES = ["Pedagogy", "Leadership", "AI in Education", "STEM", "NEP", "Assessment", "Digital Learning", "Other"];

// ── Section card ──────────────────────────────────────────────────────────────
function Section({ icon: Icon, title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/70 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-[#EEF2FF] grid place-items-center shrink-0">
            <Icon className="w-3.5 h-3.5 text-[#4F46E5]" />
          </div>
          <span className="text-sm font-semibold text-[#0F172A]">{title}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="p-6">{children}</div>}
    </div>
  );
}

const Field = ({ label, required, help, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-gray-700 mb-1.5 block">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </span>
    {children}
    {help && <p className="text-xs text-gray-400 mt-1">{help}</p>}
  </label>
);

const Row = ({ cols = 2, children }) => (
  <div className={`grid gap-4 sm:grid-cols-${cols}`}>{children}</div>
);

// ── Inline list editor (highlights, outcomes, modules…) ───────────────────────
function ListEditor({ value = [], onChange, placeholder = "Add item…", label }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const t = draft.trim();
    if (t && !value.includes(t)) { onChange([...value, t]); setDraft(""); }
  };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const onKey = (e) => { if (e.key === "Enter") { e.preventDefault(); add(); } };
  return (
    <div>
      {label && <span className="text-sm font-medium text-gray-700 mb-1.5 block">{label}</span>}
      <div className="flex gap-2 mb-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={onKey}
          placeholder={placeholder} className={SI} />
        <button type="button" onClick={add}
          className="shrink-0 w-9 h-10 grid place-items-center rounded-lg bg-[#EEF2FF] text-[#4F46E5] hover:bg-[#E0E7FF]">
          <Plus className="w-4 h-4" />
        </button>
      </div>
      {value.length > 0 && (
        <div className="space-y-1.5">
          {value.map((item, i) => (
            <div key={i} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg text-sm">
              <span className="flex-1 text-gray-700">{item}</span>
              <button type="button" onClick={() => remove(i)} className="text-gray-300 hover:text-red-500">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── FAQ editor ────────────────────────────────────────────────────────────────
function FaqEditor({ value = [], onChange }) {
  const add = () => onChange([...value, { question: "", answer: "" }]);
  const update = (i, k, v) => onChange(value.map((faq, idx) => idx === i ? { ...faq, [k]: v } : faq));
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      {value.map((faq, i) => (
        <div key={i} className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">FAQ {i + 1}</span>
            <button type="button" onClick={() => remove(i)} className="ml-auto text-gray-300 hover:text-red-500">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          <input value={faq.question} onChange={(e) => update(i, "question", e.target.value)}
            placeholder="Question" className={SI} />
          <textarea value={faq.answer} onChange={(e) => update(i, "answer", e.target.value)}
            placeholder="Answer" className={`${SI} min-h-[64px] resize-y`} />
        </div>
      ))}
      <button type="button" onClick={add}
        className="inline-flex items-center gap-1.5 text-sm text-[#4F46E5] hover:text-[#4338CA] font-medium">
        <Plus className="w-4 h-4" /> Add FAQ
      </button>
    </div>
  );
}

// ── View mode display helpers ─────────────────────────────────────────────────
const VRow = ({ label, value }) => value ? (
  <div className="grid grid-cols-[140px_1fr] gap-2 text-sm py-1.5 border-b border-gray-50 last:border-0">
    <span className="text-gray-400 font-medium shrink-0">{label}</span>
    <span className="text-[#0F172A]">{value}</span>
  </div>
) : null;

// ── Main component ────────────────────────────────────────────────────────────
export default function ProgramEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [original, setOriginal] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const load = useCallback(() => {
    api.get(`/programs/${id}`)
      .then((r) => {
        const n = {
          mode: "Online",
          cta_heading: "", cta_subheading: "", cta_description: "",
          cta_book_text: "", cta_book_show: true,
          cta_feedback_text: "", cta_feedback_show: true, cta_notice: "",
          highlights: [], outcomes: [], learning_objectives: [], training_modules: [], faqs: [],
          ...r.data,
        };
        setOriginal(n); setDraft(n);
      })
      .catch(() => toast.error("Could not load program."));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const enterEdit = () => { setDraft({ ...original }); setErrors({}); setEditMode(true); };
  const cancelEdit = () => { setDraft({ ...original }); setErrors({}); setEditMode(false); };

  const validate = () => {
    const e = {};
    if (!draft.title?.trim()) e.title = "Title is required.";
    if (!draft.description?.trim()) e.description = "Description is required.";
    if (draft.duration_hours != null && draft.duration_hours < 0) e.duration_hours = "Must be ≥ 0.";
    if (draft.price != null && draft.price < 0) e.price = "Must be ≥ 0.";
    if (draft.max_participants != null && draft.max_participants < 1) e.max_participants = "Must be ≥ 1.";
    return e;
  };

  const save = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); toast.error("Please fix validation errors."); return; }
    setSaving(true);
    try {
      const payload = {
        ...draft,
        duration_hours: parseInt(draft.duration_hours) || 0,
        price: parseFloat(draft.price) || 0,
        max_participants: draft.max_participants ? parseInt(draft.max_participants) : null,
        available_seats:  draft.available_seats  ? parseInt(draft.available_seats)  : null,
        discount: parseFloat(draft.discount) || 0,
      };
      const { data } = await api.put(`/programs/${id}`, payload);
      const n = {
        mode: "Online",
        cta_heading: "", cta_subheading: "", cta_description: "",
        cta_book_text: "", cta_book_show: true,
        cta_feedback_text: "", cta_feedback_show: true, cta_notice: "",
        highlights: [], outcomes: [], learning_objectives: [], training_modules: [], faqs: [],
        ...data,
      };
      setOriginal(n); setDraft(n);
      setEditMode(false);
      toast.success("Program saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    }
    setSaving(false);
  };

  if (!draft) {
    return (
      <div className="fade-up p-8 text-center text-gray-400 text-sm">Loading program…</div>
    );
  }

  const coverSrc  = resolveImg(draft.cover_image);
  const bannerSrc = resolveImg(draft.banner_image);

  return (
    <div className="fade-up">
      {/* ── Top bar ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <Link to="/admin/programs" className="w-9 h-9 grid place-items-center rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-500">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <h1 className="font-display text-lg font-semibold text-[#0F172A] leading-tight">{draft.title}</h1>
            <p className="text-xs text-gray-400 mt-0.5">{draft.category} · {draft.duration_hours}h · {draft.level}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!editMode ? (
            <>
              <Link to={`/programs/${id}`} target="_blank"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-600 hover:bg-gray-50">
                <Eye className="w-4 h-4" /> Preview
              </Link>
              <button onClick={enterEdit} data-testid="edit-program-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#4F46E5] text-white text-sm font-medium hover:bg-[#4338CA]">
                <Edit2 className="w-4 h-4" /> Edit Program
              </button>
            </>
          ) : (
            <>
              <button onClick={cancelEdit} disabled={saving}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                <X className="w-4 h-4" /> Cancel
              </button>
              <button onClick={() => setDraft({ ...original })}
                className="text-sm text-gray-400 hover:text-gray-600 px-3 py-2">
                Reset
              </button>
              <button onClick={save} disabled={saving} data-testid="save-program-btn"
                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-[#0F172A] text-white text-sm font-medium hover:bg-[#1e293b] disabled:opacity-50">
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Changes"}
              </button>
            </>
          )}
        </div>
      </div>

      {editMode && (
        <div className="mb-4 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-sm text-amber-700">
          <Edit2 className="w-4 h-4 shrink-0" />
          Editing mode — all fields are now editable. Click <strong>Save Changes</strong> to persist.
        </div>
      )}

      <div className="grid gap-4">

        {/* ── 1. Basic Information ─────────────────────────────────────────── */}
        <Section icon={Tag} title="Basic Information">
          {editMode ? (
            <div className="grid gap-4">
              <Row cols={2}>
                <Field label="Program title" required>
                  <input value={draft.title || ""} onChange={(e) => set("title", e.target.value)}
                    className={errors.title ? `${SI} border-red-300` : SI} data-testid="prog-title" />
                  {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title}</p>}
                </Field>
                <Field label="Program code">
                  <input value={draft.program_code || ""} onChange={(e) => set("program_code", e.target.value)}
                    placeholder="e.g. MF-PED-2025" className={SI} />
                </Field>
              </Row>
              <Field label="Subtitle">
                <input value={draft.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} className={SI} />
              </Field>
              <Row cols={3}>
                <Field label="Category">
                  <select value={draft.category || ""} onChange={(e) => set("category", e.target.value)} className={SI}>
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Level">
                  <select value={draft.level || ""} onChange={(e) => set("level", e.target.value)} className={SI}>
                    {LEVELS.map((l) => <option key={l}>{l}</option>)}
                  </select>
                </Field>
                <Field label="Mode">
                  <select value={draft.mode} onChange={(e) => set("mode", e.target.value)} className={SI}>
                    {MODES.map((m) => <option key={m}>{m}</option>)}
                  </select>
                </Field>
              </Row>
              <Row cols={4}>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={!!draft.is_published} onChange={(e) => set("is_published", e.target.checked)} className="accent-[#4F46E5]" />
                  Published
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={!!draft.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-amber-500" />
                  Featured
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={draft.registration_enabled !== false} onChange={(e) => set("registration_enabled", e.target.checked)} className="accent-[#4F46E5]" />
                  Registrations open
                </label>
              </Row>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#EEF2FF] text-[#4F46E5] border border-[#C7D2FE]">{draft.category}</span>
                {draft.featured && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200"><Star className="w-2.5 h-2.5 fill-amber-500" /> Featured</span>}
                {draft.is_published ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"><CheckCircle className="w-2.5 h-2.5" /> Published</span>
                  : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">Draft</span>}
              </div>
              <VRow label="Title" value={draft.title} />
              <VRow label="Code" value={draft.program_code} />
              <VRow label="Subtitle" value={draft.subtitle} />
              <VRow label="Level" value={draft.level} />
              <VRow label="Mode" value={draft.mode} />
              <VRow label="Registration" value={draft.registration_enabled !== false ? "Open" : "Closed"} />
            </div>
          )}
        </Section>

        {/* ── 2. Images ────────────────────────────────────────────────────── */}
        <Section icon={ImageIcon} title="Images">
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Cover Image <span className="text-xs text-gray-400">(16:10 recommended)</span></p>
              {coverSrc && (
                <div className="relative mb-2 rounded-xl overflow-hidden aspect-[16/10] bg-gray-100">
                  <img
                    src={coverSrc}
                    alt="Cover"
                    style={{
                      width: "100%", height: "100%", objectFit: "cover",
                      objectPosition: `${50 + (draft.image_offset_x || 0) / 2}% ${50 + (draft.image_offset_y || 0) / 2}%`,
                      transform: `scale(${(draft.image_scale || 100) / 100})`,
                      transformOrigin: "center",
                    }}
                  />
                  {editMode && (
                    <button type="button" onClick={() => set("cover_image", "")}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 text-white grid place-items-center hover:bg-black/70">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
              {!coverSrc && <div className="rounded-xl aspect-[16/10] bg-gray-50 border border-dashed border-gray-200 flex items-center justify-center mb-2"><ImageIcon className="w-8 h-8 text-gray-300" /></div>}
              {editMode && (
                <>
                  <ImageUpload value={draft.cover_image || ""} onChange={(url) => set("cover_image", url)} testid="prog-cover" />
                  {coverSrc && (
                    <div className="mt-3 rounded-xl border border-gray-200 p-4 bg-gray-50">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Image Position</p>
                      <div className="grid gap-3">
                        <label className="block">
                          <span className="text-xs text-gray-500 flex justify-between mb-1">
                            <span>Horizontal offset</span><span className="font-mono">{draft.image_offset_x ?? 0}</span>
                          </span>
                          <input type="range" min={-100} max={100} step={1}
                            value={draft.image_offset_x ?? 0}
                            onChange={(e) => set("image_offset_x", Number(e.target.value))}
                            className="w-full h-1.5 accent-[#4F46E5]" />
                        </label>
                        <label className="block">
                          <span className="text-xs text-gray-500 flex justify-between mb-1">
                            <span>Vertical offset</span><span className="font-mono">{draft.image_offset_y ?? 0}</span>
                          </span>
                          <input type="range" min={-100} max={100} step={1}
                            value={draft.image_offset_y ?? 0}
                            onChange={(e) => set("image_offset_y", Number(e.target.value))}
                            className="w-full h-1.5 accent-[#4F46E5]" />
                        </label>
                        <label className="block">
                          <span className="text-xs text-gray-500 flex justify-between mb-1">
                            <span>Zoom ({draft.image_scale ?? 100}%)</span>
                          </span>
                          <input type="range" min={100} max={200} step={1}
                            value={draft.image_scale ?? 100}
                            onChange={(e) => set("image_scale", Number(e.target.value))}
                            className="w-full h-1.5 accent-[#4F46E5]" />
                        </label>
                        <button type="button"
                          onClick={() => { set("image_offset_x", 0); set("image_offset_y", 0); set("image_scale", 100); }}
                          className="text-xs text-[#4F46E5] hover:underline text-left w-fit">
                          Reset position
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Banner Image <span className="text-xs text-gray-400">(wide banner)</span></p>
              {bannerSrc && (
                <div className="relative mb-2 rounded-xl overflow-hidden aspect-[16/5] bg-gray-100">
                  <img src={bannerSrc} alt="Banner" className="w-full h-full object-cover" />
                  {editMode && (
                    <button type="button" onClick={() => set("banner_image", "")}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 text-white grid place-items-center hover:bg-black/70">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
              {!bannerSrc && <div className="rounded-xl aspect-[16/5] bg-gray-50 border border-dashed border-gray-200 flex items-center justify-center mb-2"><ImageIcon className="w-8 h-8 text-gray-300" /></div>}
              {editMode && (
                <ImageUpload value={draft.banner_image || ""} onChange={(url) => set("banner_image", url)} testid="prog-banner" />
              )}
            </div>
          </div>
        </Section>

        {/* ── 3. Description ───────────────────────────────────────────────── */}
        <Section icon={FileText} title="Description">
          {editMode ? (
            <div className="grid gap-4">
              <Field label="Short description" required>
                <textarea value={draft.description || ""} onChange={(e) => set("description", e.target.value)}
                  className={errors.description ? `${TA} border-red-300` : TA} />
                {errors.description && <p className="text-xs text-red-500 mt-1">{errors.description}</p>}
              </Field>
              <Field label="Full description" help="Detailed overview shown on the program page">
                <textarea value={draft.full_description || ""} onChange={(e) => set("full_description", e.target.value)} className={`${SI} min-h-[140px] resize-y`} />
              </Field>
            </div>
          ) : (
            <div className="space-y-3">
              {draft.description && <p className="text-sm text-gray-700 leading-relaxed">{draft.description}</p>}
              {draft.full_description && <p className="text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-3">{draft.full_description}</p>}
            </div>
          )}
        </Section>

        {/* ── 4. Schedule & Logistics ───────────────────────────────────────── */}
        <Section icon={Clock} title="Schedule & Logistics">
          {editMode ? (
            <div className="grid gap-4">
              <Row cols={2}>
                <Field label="Start date"><input type="date" value={draft.start_date || ""} onChange={(e) => set("start_date", e.target.value)} className={SI} /></Field>
                <Field label="End date"><input type="date" value={draft.end_date || ""} onChange={(e) => set("end_date", e.target.value)} className={SI} /></Field>
                <Field label="Registration deadline"><input type="date" value={draft.registration_deadline || ""} onChange={(e) => set("registration_deadline", e.target.value)} className={SI} /></Field>
                <Field label="Venue"><input value={draft.venue || ""} onChange={(e) => set("venue", e.target.value)} placeholder="e.g. School auditorium / Google Meet" className={SI} /></Field>
              </Row>
              <Field label="Daily schedule / Agenda">
                <textarea value={draft.daily_schedule || ""} onChange={(e) => set("daily_schedule", e.target.value)}
                  placeholder="e.g. Day 1: 9am–10am: Introduction, 10am–1pm: Session 1…" className={TA} />
              </Field>
            </div>
          ) : (
            <div>
              <VRow label="Start date" value={draft.start_date} />
              <VRow label="End date" value={draft.end_date} />
              <VRow label="Deadline" value={draft.registration_deadline} />
              <VRow label="Venue" value={draft.venue} />
              {draft.daily_schedule && <div className="mt-2 text-sm text-gray-600 whitespace-pre-line">{draft.daily_schedule}</div>}
            </div>
          )}
        </Section>

        {/* ── 5. Program Details ────────────────────────────────────────────── */}
        <Section icon={BookOpen} title="Program Details">
          {editMode ? (
            <div className="grid gap-4">
              <Row cols={3}>
                <Field label="Duration (hours)" >
                  <input type="number" min="0" value={draft.duration_hours ?? ""} onChange={(e) => set("duration_hours", e.target.value)} className={errors.duration_hours ? `${SI} border-red-300` : SI} />
                </Field>
                <Field label="Max participants">
                  <input type="number" min="1" value={draft.max_participants ?? ""} onChange={(e) => set("max_participants", e.target.value || null)} className={SI} />
                </Field>
                <Field label="Available seats">
                  <input type="number" min="0" value={draft.available_seats ?? ""} onChange={(e) => set("available_seats", e.target.value || null)} className={SI} />
                </Field>
                <Field label="Program fee (₹)">
                  <input type="number" min="0" step="0.01" value={draft.price ?? ""} onChange={(e) => set("price", e.target.value)} className={SI} />
                </Field>
                <Field label="Discount (₹)">
                  <input type="number" min="0" value={draft.discount ?? ""} onChange={(e) => set("discount", e.target.value)} className={SI} />
                </Field>
              </Row>
              <Field label="Trainer / Facilitator">
                <input value={draft.trainer || ""} onChange={(e) => set("trainer", e.target.value)} placeholder="Name of trainer(s)" className={SI} />
              </Field>
              <Field label="Faculty / Expert details">
                <textarea value={draft.faculty_details || ""} onChange={(e) => set("faculty_details", e.target.value)} className={TA} />
              </Field>
              <Field label="Eligibility criteria">
                <textarea value={draft.eligibility || ""} onChange={(e) => set("eligibility", e.target.value)} className={TA} />
              </Field>
              <Field label="Target audience">
                <textarea value={draft.target_audience || ""} onChange={(e) => set("target_audience", e.target.value)} className={TA} />
              </Field>
            </div>
          ) : (
            <div>
              <VRow label="Duration" value={draft.duration_hours ? `${draft.duration_hours} hours` : null} />
              <VRow label="Max participants" value={draft.max_participants} />
              <VRow label="Available seats" value={draft.available_seats} />
              <VRow label="Fee" value={draft.price ? `₹${draft.price}` : "Free"} />
              <VRow label="Discount" value={draft.discount ? `₹${draft.discount}` : null} />
              <VRow label="Trainer" value={draft.trainer} />
              <VRow label="Eligibility" value={draft.eligibility} />
              <VRow label="Target audience" value={draft.target_audience} />
              {draft.faculty_details && <p className="text-sm text-gray-700 mt-2 leading-relaxed">{draft.faculty_details}</p>}
            </div>
          )}
        </Section>

        {/* ── 6. Content ───────────────────────────────────────────────────── */}
        <Section icon={CheckCircle} title="Content & Outcomes">
          {editMode ? (
            <div className="grid gap-6">
              <ListEditor value={draft.highlights || []} onChange={(v) => set("highlights", v)} label="Program highlights" placeholder="Add a highlight…" />
              <ListEditor value={draft.outcomes || []} onChange={(v) => set("outcomes", v)} label="Learning outcomes" placeholder="Add an outcome…" />
              <ListEditor value={draft.learning_objectives || []} onChange={(v) => set("learning_objectives", v)} label="Learning objectives" placeholder="Add an objective…" />
              <ListEditor value={draft.training_modules || []} onChange={(v) => set("training_modules", v)} label="Training modules" placeholder="Add a module…" />
              <Field label="Requirements / Prerequisites">
                <textarea value={draft.requirements || ""} onChange={(e) => set("requirements", e.target.value)} className={TA} />
              </Field>
              <Field label="Materials included">
                <textarea value={draft.materials_included || ""} onChange={(e) => set("materials_included", e.target.value)} className={TA} />
              </Field>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-6">
              {draft.highlights?.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Highlights</p>
                  <ul className="space-y-1">{draft.highlights.map((h, i) => <li key={i} className="text-sm text-gray-700 flex items-start gap-2"><span className="text-emerald-500 mt-0.5">✓</span>{h}</li>)}</ul>
                </div>
              )}
              {draft.outcomes?.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Outcomes</p>
                  <ul className="space-y-1">{draft.outcomes.map((h, i) => <li key={i} className="text-sm text-gray-700 flex items-start gap-2"><span className="text-[#4F46E5] mt-0.5">✓</span>{h}</li>)}</ul>
                </div>
              )}
              {draft.training_modules?.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Modules</p>
                  <ul className="space-y-1">{draft.training_modules.map((m, i) => <li key={i} className="text-sm text-gray-700">{i + 1}. {m}</li>)}</ul>
                </div>
              )}
            </div>
          )}
        </Section>

        {/* ── 7. Certification & Assessment ────────────────────────────────── */}
        <Section icon={Award} title="Certification & Assessment" defaultOpen={false}>
          {editMode ? (
            <div className="grid gap-4">
              <Row cols={2}>
                <Field label="Certificate type" help="e.g. Participation, Completion, Excellence">
                  <input value={draft.certificate_type || ""} onChange={(e) => set("certificate_type", e.target.value)}
                    placeholder="e.g. Certificate of Participation" className={SI} />
                </Field>
                <Field label="Handouts / Materials provided">
                  <input value={draft.handouts || ""} onChange={(e) => set("handouts", e.target.value)}
                    placeholder="e.g. Printed workbook, USB drive" className={SI} />
                </Field>
              </Row>
              <Field label="Certificate details">
                <textarea value={draft.certificate_details || ""} onChange={(e) => set("certificate_details", e.target.value)} className={TA} />
              </Field>
              <Field label="Assessment details">
                <textarea value={draft.assessment_details || ""} onChange={(e) => set("assessment_details", e.target.value)} className={TA} />
              </Field>
            </div>
          ) : (
            <div>
              <VRow label="Certificate type" value={draft.certificate_type} />
              <VRow label="Handouts" value={draft.handouts} />
              <VRow label="Certificate" value={draft.certificate_details} />
              <VRow label="Assessment" value={draft.assessment_details} />
            </div>
          )}
        </Section>

        {/* ── 8. FAQs ──────────────────────────────────────────────────────── */}
        <Section icon={MessageSquare} title="FAQs" defaultOpen={false}>
          {editMode ? (
            <FaqEditor value={draft.faqs || []} onChange={(v) => set("faqs", v)} />
          ) : (
            draft.faqs?.length > 0 ? (
              <div className="space-y-3">
                {draft.faqs.map((faq, i) => (
                  <div key={i} className="rounded-xl bg-gray-50 border border-gray-100 p-4">
                    <p className="text-sm font-medium text-[#0F172A]">{faq.question}</p>
                    <p className="text-sm text-gray-600 mt-1 leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-gray-400">No FAQs added.</p>
          )}
        </Section>

        {/* ── 9. CTA Card ──────────────────────────────────────────────────── */}
        <Section icon={Globe} title="CTA Card (Sidebar)" defaultOpen={true}>
          {editMode ? (
            <div className="grid gap-4">
              <Field label="Heading" help="e.g. Bring this to your school">
                <input value={draft.cta_heading || ""} onChange={(e) => set("cta_heading", e.target.value)} placeholder="Bring this to your school" className={SI} />
              </Field>
              <Field label="Subheading" help="e.g. Custom cohort">
                <input value={draft.cta_subheading || ""} onChange={(e) => set("cta_subheading", e.target.value)} placeholder="Custom cohort" className={SI} />
              </Field>
              <Field label="Description" help="Short line under subheading">
                <input value={draft.cta_description || ""} onChange={(e) => set("cta_description", e.target.value)} placeholder="Delivered on your dates. Certificate for every teacher." className={SI} />
              </Field>
              <Row cols={2}>
                <div>
                  <label className="flex items-center gap-2 text-sm text-gray-700 mb-2 cursor-pointer">
                    <input type="checkbox" checked={draft.cta_book_show !== false} onChange={(e) => set("cta_book_show", e.target.checked)} className="accent-[#4F46E5]" />
                    Show Book Now button
                  </label>
                  <input value={draft.cta_book_text || ""} onChange={(e) => set("cta_book_text", e.target.value)} placeholder="Book this training" className={SI} />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm text-gray-700 mb-2 cursor-pointer">
                    <input type="checkbox" checked={draft.cta_feedback_show !== false} onChange={(e) => set("cta_feedback_show", e.target.checked)} className="accent-emerald-500" />
                    Show Feedback button
                  </label>
                  <input value={draft.cta_feedback_text || ""} onChange={(e) => set("cta_feedback_text", e.target.value)} placeholder="Submit Feedback & Claim Certificate" className={SI} />
                </div>
              </Row>
              <Field label="Certificate notice" help="Small text below feedback button">
                <input value={draft.cta_notice || ""} onChange={(e) => set("cta_notice", e.target.value)} placeholder="Certificate is issued after admin approval and emailed to you." className={SI} />
              </Field>
            </div>
          ) : (
            <div>
              <VRow label="Heading" value={draft.cta_heading || "Bring this to your school"} />
              <VRow label="Subheading" value={draft.cta_subheading || "Custom cohort"} />
              <VRow label="Description" value={draft.cta_description || "Delivered on your dates. Certificate for every teacher."} />
              <VRow label="Book button" value={draft.cta_book_show !== false ? (draft.cta_book_text || "Book this training") : "Hidden"} />
              <VRow label="Feedback button" value={draft.cta_feedback_show !== false ? (draft.cta_feedback_text || "Submit Feedback & Claim Certificate") : "Hidden"} />
              <VRow label="Notice" value={draft.cta_notice} />
            </div>
          )}
        </Section>

        {/* ── 10. SEO ───────────────────────────────────────────────────────── */}
        <Section icon={Search} title="SEO" defaultOpen={false}>
          {editMode ? (
            <div className="grid gap-4">
              <Field label="SEO title" help="Appears in browser tab and search results">
                <input value={draft.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} className={SI} placeholder={draft.title} />
              </Field>
              <Field label="SEO description" help="160 characters recommended">
                <textarea value={draft.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} className={`${SI} min-h-[72px] resize-y`} />
              </Field>
              <Field label="SEO keywords" help="Comma-separated">
                <input value={draft.seo_keywords || ""} onChange={(e) => set("seo_keywords", e.target.value)} placeholder="teacher training, Manthan Foundation, …" className={SI} />
              </Field>
            </div>
          ) : (
            <div>
              <VRow label="SEO title" value={draft.seo_title} />
              <VRow label="Description" value={draft.seo_description} />
              <VRow label="Keywords" value={draft.seo_keywords} />
            </div>
          )}
        </Section>

      </div>

      {/* Bottom save bar in edit mode */}
      {editMode && (
        <div className="sticky bottom-0 left-0 right-0 mt-6 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-white/95 backdrop-blur-sm border-t border-gray-200 flex items-center justify-end gap-3 z-10">
          <button onClick={cancelEdit} disabled={saving}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={save} disabled={saving} data-testid="save-program-bottom"
            className="inline-flex items-center gap-1.5 px-6 py-2 rounded-lg bg-[#0F172A] text-white text-sm font-medium hover:bg-[#1e293b] disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      )}
    </div>
  );
}
